import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Plus, Trash, X } from "@phosphor-icons/react";

const CLASSIFICATIONS = ["Active", "Passive", "Network", "Consumable", "IT"];
const UOMS = ["EA", "BOX", "MTR", "KG", "LTR"];

export default function ProductMasterPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: "", name: "", description: "", classification: "Active",
    category: "", manufacturer: "", mfg_part_number: "", oss_mapping: "",
    uom: "EA", substitutes: "", cross_refs: "",
  });

  const load = async () => { const { data } = await api.get("/alm/products"); setRows(data); };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      substitutes: form.substitutes ? form.substitutes.split(",").map(s => s.trim()).filter(Boolean) : [],
      cross_refs: form.cross_refs ? form.cross_refs.split(",").map(s => s.trim()).filter(Boolean) : [],
    };
    await api.post("/alm/products", payload);
    setOpen(false);
    setForm({ ...form, code: "", name: "", description: "", category: "", manufacturer: "", mfg_part_number: "", oss_mapping: "", substitutes: "", cross_refs: "" });
    load();
  };

  const remove = async (id) => { if (window.confirm("Delete this product?")) { await api.delete(`/alm/products/${id}`); load(); } };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/alm/products/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
    alert(`Imported ${data.imported}, skipped ${data.skipped}${data.errors?.length ? `\nErrors:\n${data.errors.join("\n")}` : ""}`);
    e.target.value = "";
    load();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Asset Lifecycle"
        title="Product Master"
        subtitle="Define products, classifications, manufacturer references and substitutes."
        testid="alm-products-title"
        actions={
          <>
            <label data-testid="alm-product-import-btn" className="border border-gray-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-gray-50 inline-flex items-center gap-2 cursor-pointer">
              Import CSV
              <input type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" data-testid="alm-product-import-input" />
            </label>
            <button onClick={() => setOpen(true)} data-testid="alm-product-new-btn" className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] flex items-center gap-2">
              <Plus size={14} weight="bold" /> New Product
            </button>
          </>
        }
      />
      <div className="p-8">
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="alm-products-table">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {["Code", "Name", "Classification", "Manufacturer", "Part #", "UOM", "Substitutes", "OSS Map", ""].map(h => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs font-bold">{r.code}</td>
                  <td className="px-4 py-3 font-semibold">{r.name}</td>
                  <td className="px-4 py-3 text-xs">{r.classification}</td>
                  <td className="px-4 py-3 text-xs">{r.manufacturer}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.mfg_part_number}</td>
                  <td className="px-4 py-3 text-xs">{r.uom}</td>
                  <td className="px-4 py-3 text-xs">{(r.substitutes || []).join(", ")}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.oss_mapping}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(r.id)} data-testid={`alm-product-del-${r.id}`} className="text-[#DC2626] hover:underline text-xs font-bold inline-flex items-center gap-1">
                      <Trash size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-sm text-gray-500">No products. Click <strong>New Product</strong> to create one.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={() => setOpen(false)}>
          <form onClick={e => e.stopPropagation()} onSubmit={create} className="w-full max-w-md bg-white h-full overflow-y-auto" data-testid="alm-product-form">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Create</div>
                <h2 className="text-2xl font-black tracking-tight">New Product</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              {[
                ["code", "Product Code *", "text", true],
                ["name", "Name *", "text", true],
                ["description", "Description", "text", false],
                ["category", "Category", "text", false],
                ["manufacturer", "Manufacturer", "text", false],
                ["mfg_part_number", "Manufacturer Part #", "text", false],
                ["oss_mapping", "OSS Mapping", "text", false],
                ["substitutes", "Substitute Codes (comma-sep)", "text", false],
                ["cross_refs", "Cross References (comma-sep)", "text", false],
              ].map(([k, label, type, required]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">{label}</label>
                  <input type={type} required={required} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`alm-product-${k}`} className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Classification</label>
                  <select value={form.classification} onChange={e => setForm({ ...form, classification: e.target.value })} data-testid="alm-product-classification" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                    {CLASSIFICATIONS.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">UOM</label>
                  <select value={form.uom} onChange={e => setForm({ ...form, uom: e.target.value })} data-testid="alm-product-uom" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                    {UOMS.map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200">
              <button type="submit" data-testid="alm-product-submit" className="w-full bg-[#0020C2] text-white py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#001AA3]">Create Product</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
